import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Fuel, Plus, RefreshCw } from "lucide-react";
import { AuthContext } from "../Context/AuthContext";
import { useToast } from "../Context/ToastContext";
import {
  apiMessage,
  approveFuelBill,
  createFuelBill,
  deleteFuelBill,
  duplicateFromError,
  getFuelAllocations,
  getFuelBills,
  getFuelOverview,
  getFuelSettings,
  listEmployees,
  putFuelEligibility,
  putFuelSettings,
  rejectFuelBill,
  updateFuelBill,
} from "./allowancesApi";
import {
  ConfirmShell,
  Drawer,
  DuplicateRecordDialog,
  EmptyState,
  FeatureDisabledBanner,
  FeatureHeader,
  OrgToggle,
  PaginationBar,
  StatusBadge,
  SuccessFlash,
  SummaryCards,
  Tabs,
  currentMonthValue,
  formatDate,
  formatMoney,
  mediaUrl,
  roleFlags,
  sameDate,
  sameMoney,
} from "./shared";
import "../../styles/allowances-benefits.css";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "employees", label: "Employees" },
  { id: "bills", label: "Fuel Bills" },
  { id: "history", label: "History" },
];

const EMPTY_BILL = {
  employee_id: "",
  bill_date: "",
  amount: "",
  bill_number: "",
  remarks: "",
  attachment: null,
};

const PAGE_SIZE = 25;
const MAX_BILL_BYTES = 10 * 1024 * 1024;

function monthFromDate(value) {
  const raw = String(value || "").slice(0, 10);
  if (raw.length < 7) return currentMonthValue();
  return raw.slice(0, 7);
}

function employeeLabel(emp) {
  if (!emp) return "this employee";
  return (
    emp.full_name ||
    [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
    emp.employee_name ||
    emp.employee_code ||
    "this employee"
  );
}

/** Same employee + same date = duplicate (amount may differ). */
function isDuplicateFuelMatch(bill, { employeeId, billDate, excludeId }) {
  if (!bill) return false;
  if (excludeId && String(bill.id) === String(excludeId)) return false;
  if (String(bill.employee_id) !== String(employeeId)) return false;
  return sameDate(bill.bill_date, billDate);
}

async function findDuplicateFuelBill({ employeeId, billDate, amount, excludeId, localBills = [] }) {
  const candidates = (localBills || []).filter((bill) =>
    isDuplicateFuelMatch(bill, { employeeId, billDate, excludeId })
  );
  if (candidates.length) {
    const amountHit = candidates.find((bill) => sameMoney(bill.amount, amount));
    return amountHit || candidates[0];
  }

  const dateKey = String(billDate || "").slice(0, 10);
  try {
    const data = await getFuelBills({
      employee_id: employeeId,
      date_from: dateKey,
      date_to: dateKey,
      limit: 200,
      offset: 0,
    });
    const rows = (data?.bills || []).filter((bill) =>
      isDuplicateFuelMatch(bill, { employeeId, billDate, excludeId })
    );
    if (!rows.length) return null;
    const amountHit = rows.find((bill) => sameMoney(bill.amount, amount));
    return amountHit || rows[0];
  } catch {
    return null;
  }
}

export default function FuelReimbursementPage() {
  const toast = useToast();
  const { user: me } = useContext(AuthContext);
  const role = me?.role || me?.user?.role || "";
  const { canManage } = roleFlags(role);

  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [overview, setOverview] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [billsPayload, setBillsPayload] = useState({ total: 0, bills: [] });
  const [filters, setFilters] = useState({
    status: "",
    employee_id: "",
    month: currentMonthValue(),
    offset: 0,
  });
  const [historyFilters, setHistoryFilters] = useState({
    status: "APPROVED",
    month: currentMonthValue(),
    offset: 0,
  });
  const [historyPayload, setHistoryPayload] = useState({ total: 0, bills: [] });

  const [eligDrawer, setEligDrawer] = useState(null);
  const [billDrawer, setBillDrawer] = useState(null);
  const [billForm, setBillForm] = useState(EMPTY_BILL);
  const [rejectDrawer, setRejectDrawer] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [empSearch, setEmpSearch] = useState("");
  const [showAllAlloc, setShowAllAlloc] = useState(false);
  const [successFlash, setSuccessFlash] = useState(null);
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);

  const loadCore = useCallback(async () => {
    setLoading(true);
    try {
      const [settings, ov, alloc, empRes] = await Promise.all([
        getFuelSettings(),
        getFuelOverview().catch(() => null),
        getFuelAllocations({ eligibleOnly: !showAllAlloc }).catch(() => ({ allocations: [] })),
        listEmployees({ active_only: true, page_size: 500 }).catch(() => []),
      ]);
      setEnabled(Boolean(settings?.enabled));
      setOverview(ov);
      setAllocations(alloc?.allocations || []);
      const empList = Array.isArray(empRes) ? empRes : empRes?.items || [];
      setEmployees(empList);
    } catch (err) {
      toast.error(apiMessage(err, "Unable to load fuel reimbursement."));
    } finally {
      setLoading(false);
    }
  }, [showAllAlloc, toast]);

  const loadBills = useCallback(async () => {
    try {
      const [year, month] = (filters.month || "").split("-").map(Number);
      const params = {
        limit: PAGE_SIZE,
        offset: filters.offset,
        status: filters.status || undefined,
        employee_id: filters.employee_id || undefined,
      };
      if (year && month) {
        params.year = year;
        params.month = month;
      }
      const data = await getFuelBills(params);
      setEnabled(Boolean(data?.enabled ?? enabled));
      setBillsPayload({ total: data?.total || 0, bills: data?.bills || [] });
    } catch (err) {
      toast.error(apiMessage(err, "Unable to load fuel bills."));
    }
  }, [enabled, filters, toast]);

  const loadHistory = useCallback(async () => {
    try {
      const [year, month] = (historyFilters.month || "").split("-").map(Number);
      const params = {
        limit: PAGE_SIZE,
        offset: historyFilters.offset,
        status: historyFilters.status || undefined,
      };
      if (year && month) {
        params.year = year;
        params.month = month;
      }
      const data = await getFuelBills(params);
      setHistoryPayload({ total: data?.total || 0, bills: data?.bills || [] });
    } catch (err) {
      toast.error(apiMessage(err, "Unable to load fuel history."));
    }
  }, [historyFilters, toast]);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  useEffect(() => {
    if (tab === "bills") loadBills();
  }, [tab, loadBills]);

  useEffect(() => {
    if (tab === "history") loadHistory();
  }, [tab, loadHistory]);

  const cards = useMemo(() => {
    const o = overview || {};
    return [
      { label: "Eligible employees", value: o.eligible_employees ?? "—", accent: true },
      { label: "Pending bills", value: o.pending_bills ?? "—", warn: true },
      { label: "Approved this month", value: formatMoney(o.approved_this_month) },
      { label: "Reimbursed this month", value: formatMoney(o.reimbursed_this_month) },
    ];
  }, [overview]);

  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    const allocMap = new Map(allocations.map((a) => [String(a.employee_id), a]));
    return employees
      .map((emp) => {
        const id = emp.employee_id || emp.id;
        const alloc = allocMap.get(String(id));
        return {
          employee_id: id,
          employee_name:
            emp.full_name ||
            [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
            emp.employee_name ||
            "—",
          employee_code: emp.employee_code,
          department_name: emp.department_name || emp.department?.name,
          eligible: alloc ? Boolean(alloc.eligible) : false,
          monthly_limit: alloc?.monthly_limit ?? null,
          used_this_month: alloc?.used_this_month ?? 0,
          remaining: alloc?.remaining ?? null,
          status: alloc?.status || (alloc?.eligible ? "Active" : "Inactive"),
        };
      })
      .filter((row) => {
        if (!q) return true;
        return (
          String(row.employee_name).toLowerCase().includes(q) ||
          String(row.employee_code || "").toLowerCase().includes(q)
        );
      });
  }, [allocations, employees, empSearch]);

  async function toggleEnabled(next) {
    if (!canManage) return;
    setSaving(true);
    try {
      const res = await putFuelSettings(next);
      setEnabled(Boolean(res?.enabled));
      toast.success(next ? "Fuel reimbursement enabled." : "Fuel reimbursement disabled.");
      await loadCore();
    } catch (err) {
      toast.error(apiMessage(err, "Unable to update fuel settings."));
    } finally {
      setSaving(false);
    }
  }

  function openEligibility(row) {
    setEligDrawer({
      employee_id: row.employee_id,
      employee_name: row.employee_name,
      eligible: Boolean(row.eligible),
      monthly_limit: row.monthly_limit == null ? "" : String(row.monthly_limit),
    });
  }

  async function saveEligibility(e) {
    e.preventDefault();
    if (!canManage || !enabled || !eligDrawer) return;
    setSaving(true);
    try {
      await putFuelEligibility(eligDrawer.employee_id, {
        eligible: eligDrawer.eligible,
        monthly_limit: eligDrawer.monthly_limit,
      });
      toast.success("Fuel eligibility saved.");
      setEligDrawer(null);
      await loadCore();
    } catch (err) {
      toast.error(apiMessage(err, "Unable to save eligibility."));
    } finally {
      setSaving(false);
    }
  }

  function openAddBill(prefillEmployeeId) {
    setDuplicatePrompt(null);
    setBillForm({
      ...EMPTY_BILL,
      employee_id: prefillEmployeeId || "",
      bill_date: new Date().toISOString().slice(0, 10),
    });
    setBillDrawer({ mode: "create" });
  }

  function openEditBill(bill) {
    setDuplicatePrompt(null);
    setBillForm({
      employee_id: bill.employee_id,
      bill_date: String(bill.bill_date || "").slice(0, 10),
      amount: String(bill.amount ?? ""),
      bill_number: bill.bill_number || "",
      remarks: bill.remarks || "",
      attachment: null,
    });
    setBillDrawer({ mode: "edit", id: bill.id, bill });
  }

  function showBillSuccess(isEdit) {
    const emp =
      employees.find((e) => String(e.employee_id || e.id) === String(billForm.employee_id)) || {};
    const empName = employeeLabel(emp);
    setSuccessFlash({
      title: isEdit ? "Fuel bill updated successfully" : "Fuel bill successfully added",
      message: isEdit
        ? `Changes for ${empName} are saved. Pending bills still need approval before payroll.`
        : `Bill for ${empName} is saved and pending until HR / Admin approves.`,
    });
    toast.success(isEdit ? "Fuel bill updated successfully." : "Fuel bill successfully added.");
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  function openDuplicatePrompt(record, { mode = "create", pendingBillId = null } = {}) {
    const emp =
      employees.find((e) => String(e.employee_id || e.id) === String(record?.employee_id || billForm.employee_id)) ||
      {};
    const name = record?.employee_name || employeeLabel(emp);
    setDuplicatePrompt({ record, employeeName: name, mode, pendingBillId });
    toast.error(
      mode === "approve"
        ? `Duplicate approved bill for ${name} on this date. Confirm to approve anyway.`
        : `Duplicate bill for ${name} on this date. Choose Allow duplicate or Don't add.`
    );
  }

  async function performSaveBill({ allowDuplicate = false } = {}) {
    if (!canManage || !enabled) return;
    const amount = Number(billForm.amount);
    if (!billForm.employee_id) {
      toast.error("Select an employee.");
      return;
    }
    if (!billForm.bill_date) {
      toast.error("Bill date is required.");
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Amount must be greater than 0.");
      return;
    }
    if (billForm.attachment && billForm.attachment.size > MAX_BILL_BYTES) {
      toast.error("The bill file is too large. Use a PDF, JPG, or PNG under 10 MB.");
      return;
    }

    const isEdit = billDrawer?.mode === "edit";
    setSaving(true);
    try {
      if (!allowDuplicate) {
        const existing = await findDuplicateFuelBill({
          employeeId: billForm.employee_id,
          billDate: billForm.bill_date,
          amount,
          excludeId: isEdit ? billDrawer.id : null,
          localBills: [...(billsPayload.bills || []), ...(historyPayload.bills || [])],
        });
        if (existing) {
          openDuplicatePrompt(existing, { mode: "create" });
          setSaving(false);
          return;
        }
      }

      if (isEdit) {
        await updateFuelBill(billDrawer.id, {
          bill_date: billForm.bill_date,
          amount,
          bill_number: billForm.bill_number || null,
          remarks: billForm.remarks || null,
        });
      } else {
        const body = new FormData();
        body.append("bill_date", billForm.bill_date);
        body.append("amount", String(amount));
        if (billForm.bill_number) body.append("bill_number", billForm.bill_number);
        if (billForm.remarks) body.append("remarks", billForm.remarks);
        if (billForm.attachment) body.append("attachment", billForm.attachment);
        if (allowDuplicate) body.append("allow_duplicate", "true");
        await createFuelBill(billForm.employee_id, body);
      }

      showBillSuccess(isEdit);
      setDuplicatePrompt(null);
      const billMonth = monthFromDate(billForm.bill_date);
      setBillDrawer(null);
      setBillForm(EMPTY_BILL);
      setFilters((f) => ({
        ...f,
        month: billMonth,
        employee_id: "",
        status: "",
        offset: 0,
      }));
      setTab("bills");
      await loadCore();
    } catch (err) {
      const dup = duplicateFromError(err);
      if (dup) {
        openDuplicatePrompt(dup, { mode: "create" });
        return;
      }
      toast.error(apiMessage(err, "Unable to save fuel bill."));
    } finally {
      setSaving(false);
    }
  }

  async function saveBill(e) {
    e.preventDefault();
    await performSaveBill();
  }

  async function allowDuplicateBill() {
    const prompt = duplicatePrompt;
    setDuplicatePrompt(null);
    if (prompt?.mode === "approve" && prompt.pendingBillId) {
      await performApprove(prompt.pendingBillId, { allowDuplicate: true });
      return;
    }
    await performSaveBill({ allowDuplicate: true });
  }

  async function performApprove(billId, { allowDuplicate = false } = {}) {
    if (!canManage || !enabled) return;
    setSaving(true);
    try {
      if (!allowDuplicate) {
        const bill = (billsPayload.bills || []).find((b) => String(b.id) === String(billId));
        if (bill) {
          const dateKey = String(bill.bill_date || "").slice(0, 10);
          let approvedHit = (billsPayload.bills || []).find(
            (b) =>
              String(b.id) !== String(billId) &&
              String(b.employee_id) === String(bill.employee_id) &&
              sameDate(b.bill_date, bill.bill_date) &&
              String(b.status || "").toUpperCase() === "APPROVED"
          );
          if (!approvedHit) {
            try {
              const data = await getFuelBills({
                employee_id: bill.employee_id,
                date_from: dateKey,
                date_to: dateKey,
                status: "APPROVED",
                limit: 50,
                offset: 0,
              });
              approvedHit = (data?.bills || []).find((b) => String(b.id) !== String(billId));
            } catch {
              approvedHit = null;
            }
          }
          if (approvedHit) {
            openDuplicatePrompt(approvedHit, { mode: "approve", pendingBillId: billId });
            setSaving(false);
            return;
          }
        }
      }

      await approveFuelBill(billId, { allowDuplicate });
      toast.success("Fuel bill approved.");
      setRejectDrawer(null);
      setDuplicatePrompt(null);
      await Promise.all([loadCore(), loadBills()]);
    } catch (err) {
      const dup = duplicateFromError(err);
      if (dup) {
        openDuplicatePrompt(dup, { mode: "approve", pendingBillId: billId });
        return;
      }
      toast.error(apiMessage(err, "Unable to approve bill."));
      await loadBills().catch(() => {});
    } finally {
      setSaving(false);
    }
  }

  async function onApprove(billId) {
    await performApprove(billId);
  }

  async function onReject(e) {
    e.preventDefault();
    if (!canManage || !rejectDrawer) return;
    if (!rejectReason.trim()) {
      toast.error("Rejection reason is required.");
      return;
    }
    setSaving(true);
    try {
      await rejectFuelBill(rejectDrawer.id, rejectReason.trim());
      toast.success("Fuel bill rejected.");
      setRejectDrawer(null);
      setRejectReason("");
      await Promise.all([loadCore(), loadBills()]);
    } catch (err) {
      toast.error(apiMessage(err, "Unable to reject bill."));
      await loadBills().catch(() => {});
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!canManage || !deleteTarget) return;
    setSaving(true);
    try {
      await deleteFuelBill(deleteTarget.id);
      toast.success("Fuel bill deleted.");
      setDeleteTarget(null);
      await Promise.all([loadCore(), loadBills()]);
    } catch (err) {
      toast.error(apiMessage(err, "Unable to delete bill."));
    } finally {
      setSaving(false);
    }
  }

  const breadcrumb = (
    <>
      <Link to="/settings">Settings</Link>
      <span>/</span>
      <Link to="/settings/allowances-benefits">Allowances & Benefits</Link>
      <span>/</span>
      <span>Fuel Reimbursement</span>
    </>
  );

  if (loading && !overview) {
    return (
      <div className="ab-page">
        <div className="ab-loading">Loading fuel reimbursement…</div>
      </div>
    );
  }

  return (
    <div className="ab-page">
      <FeatureHeader
        icon={Fuel}
        title="Fuel Reimbursement"
        subtitle="Eligibility, petrol bills, and approvals. Only approved amounts are paid in payroll."
        breadcrumb={breadcrumb}
        actions={
          <>
            <button type="button" className="ab-btn ab-btn--secondary" onClick={loadCore} disabled={loading}>
              <RefreshCw size={14} /> Refresh
            </button>
            {canManage && enabled ? (
              <button type="button" className="ab-btn ab-btn--primary" onClick={() => openAddBill()}>
                <Plus size={14} /> Add fuel bill
              </button>
            ) : null}
          </>
        }
      />

      <SuccessFlash
        open={Boolean(successFlash)}
        title={successFlash?.title}
        message={successFlash?.message}
        onDismiss={() => setSuccessFlash(null)}
      />

      <FeatureDisabledBanner
        featureName="Fuel reimbursement"
        managePath="/salary-v2/setup"
        enabled={enabled}
        canManage={canManage}
      />

      {canManage ? (
        <div className="ab-panel">
          <div className="ab-panel__body">
            <OrgToggle
              label="Organisation fuel reimbursement"
              hint="When off, new bills and eligibility changes are blocked. History stays available."
              checked={enabled}
              disabled={saving}
              onChange={toggleEnabled}
            />
          </div>
        </div>
      ) : null}

      <SummaryCards items={cards} />

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === "overview" ? (
        <div className="ab-panel">
          <div className="ab-panel__head">
            <div>
              <h2>How it works</h2>
              <p>Mark employees eligible, collect bills, approve, and reimburse via payroll.</p>
            </div>
          </div>
          <div className="ab-panel__body">
            <ol className="ab-note" style={{ paddingLeft: "1.1rem", display: "grid", gap: "0.45rem" }}>
              <li>Enable the organisation toggle above (or in Payroll Settings).</li>
              <li>Mark employees eligible and set an optional monthly limit.</li>
              <li>Submit petrol bills with optional attachments.</li>
              <li>Approve or reject pending bills. Only approved amounts pay in that month.</li>
            </ol>
            <div className="ab-row-actions" style={{ marginTop: "1rem" }}>
              <button type="button" className="ab-btn ab-btn--secondary" onClick={() => setTab("employees")}>
                Manage employees
              </button>
              <button type="button" className="ab-btn ab-btn--secondary" onClick={() => setTab("bills")}>
                Review fuel bills
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {tab === "employees" ? (
        <div className="ab-panel">
          <div className="ab-panel__head">
            <div>
              <h2>Employee eligibility</h2>
              <p>Control who can submit fuel bills and their monthly limit.</p>
            </div>
          </div>
          <div className="ab-panel__body">
            <div className="ab-toolbar">
              <div className="ab-field" style={{ minWidth: 200, flex: 1 }}>
                <label htmlFor="fuel-emp-search">Search</label>
                <input
                  id="fuel-emp-search"
                  className="ab-input"
                  placeholder="Name or code"
                  value={empSearch}
                  onChange={(e) => setEmpSearch(e.target.value)}
                />
              </div>
              <label className="ab-check" style={{ marginBottom: 2 }}>
                <input
                  type="checkbox"
                  checked={showAllAlloc}
                  onChange={(e) => setShowAllAlloc(e.target.checked)}
                />
                <span>
                  <strong>Show all employees</strong>
                  <small>Include non-eligible in allocation fetch</small>
                </span>
              </label>
            </div>
            {filteredEmployees.length === 0 ? (
              <EmptyState title="No employees found" description="Adjust search or ensure employees are active." />
            ) : (
              <div className="ab-table-wrap">
                <table className="ab-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Department</th>
                      <th>Eligible</th>
                      <th>Monthly limit</th>
                      <th>Used / remaining</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((row) => (
                      <tr key={row.employee_id}>
                        <td>
                          <strong>{row.employee_name}</strong>
                          <div className="ab-note">{row.employee_code || "—"}</div>
                        </td>
                        <td>{row.department_name || "—"}</td>
                        <td>
                          <StatusBadge
                            status={row.eligible ? "eligible" : "inactive"}
                            label={row.eligible ? "Eligible" : "Not eligible"}
                          />
                        </td>
                        <td>{row.monthly_limit == null ? "No limit" : formatMoney(row.monthly_limit)}</td>
                        <td>
                          {formatMoney(row.used_this_month)}
                          {" / "}
                          {row.remaining == null ? "—" : formatMoney(row.remaining)}
                        </td>
                        <td>
                          {canManage ? (
                            <button
                              type="button"
                              className="ab-btn ab-btn--ghost ab-btn--sm"
                              disabled={!enabled}
                              onClick={() => openEligibility(row)}
                            >
                              Edit
                            </button>
                          ) : (
                            <span className="ab-note">View only</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {tab === "bills" ? (
        <div className="ab-panel">
          <div className="ab-panel__head">
            <div>
              <h2>Fuel bills</h2>
              <p>Filter, review, approve, or reject pending petrol bills.</p>
            </div>
          </div>
          <div className="ab-panel__body">
            <div className="ab-toolbar">
              <div className="ab-field">
                <label>Month</label>
                <input
                  type="month"
                  className="ab-input"
                  value={filters.month}
                  onChange={(e) => setFilters((f) => ({ ...f, month: e.target.value, offset: 0 }))}
                />
              </div>
              <div className="ab-field">
                <label>Status</label>
                <select
                  className="ab-select"
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, offset: 0 }))}
                >
                  <option value="">All</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
              <div className="ab-field" style={{ minWidth: 180 }}>
                <label>Employee</label>
                <select
                  className="ab-select"
                  value={filters.employee_id}
                  onChange={(e) => setFilters((f) => ({ ...f, employee_id: e.target.value, offset: 0 }))}
                >
                  <option value="">All employees</option>
                  {employees.map((emp) => {
                    const id = emp.employee_id || emp.id;
                    const name =
                      emp.full_name ||
                      [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
                      emp.employee_code;
                    return (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {billsPayload.bills.length === 0 ? (
              <EmptyState
                icon={Fuel}
                title="No fuel bills"
                description="Add a bill for an eligible employee, or adjust filters."
                action={
                  canManage && enabled ? (
                    <button type="button" className="ab-btn ab-btn--primary" onClick={() => openAddBill()}>
                      Add fuel bill
                    </button>
                  ) : null
                }
              />
            ) : (
              <>
                <div className="ab-table-wrap">
                  <table className="ab-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Bill date</th>
                        <th>Bill no.</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billsPayload.bills.map((bill) => {
                        const st = String(bill.status || "").toUpperCase();
                        return (
                          <tr key={bill.id}>
                            <td>
                              <strong>{bill.employee_name || "—"}</strong>
                              <div className="ab-note">{bill.employee_code || ""}</div>
                            </td>
                            <td>{formatDate(bill.bill_date)}</td>
                            <td>{bill.bill_number || "—"}</td>
                            <td>{formatMoney(bill.amount)}</td>
                            <td>
                              <StatusBadge status={st} />
                            </td>
                            <td>
                              <div className="ab-row-actions">
                                {canManage && enabled && st === "PENDING" ? (
                                  <>
                                    <button
                                      type="button"
                                      className="ab-btn ab-btn--primary ab-btn--sm"
                                      disabled={saving}
                                      onClick={() => onApprove(bill.id)}
                                    >
                                      Approve
                                    </button>
                                    <button
                                      type="button"
                                      className="ab-btn ab-btn--secondary ab-btn--sm"
                                      disabled={saving}
                                      onClick={() => {
                                        setRejectDrawer(bill);
                                        setRejectReason("");
                                      }}
                                    >
                                      Reject
                                    </button>
                                    <button
                                      type="button"
                                      className="ab-btn ab-btn--ghost ab-btn--sm"
                                      onClick={() => openEditBill(bill)}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      className="ab-btn ab-btn--ghost ab-btn--sm"
                                      onClick={() => setDeleteTarget(bill)}
                                    >
                                      Delete
                                    </button>
                                  </>
                                ) : null}
                                {bill.attachment ? (
                                  <a
                                    className="ab-btn ab-btn--ghost ab-btn--sm"
                                    href={mediaUrl(bill.attachment)}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Attachment
                                  </a>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <PaginationBar
                  total={billsPayload.total}
                  limit={PAGE_SIZE}
                  offset={filters.offset}
                  onChange={(offset) => setFilters((f) => ({ ...f, offset }))}
                />
              </>
            )}
          </div>
        </div>
      ) : null}

      {tab === "history" ? (
        <div className="ab-panel">
          <div className="ab-panel__head">
            <div>
              <h2>History</h2>
              <p>Approved and rejected bills for the selected month. Visible even when the feature is off.</p>
            </div>
          </div>
          <div className="ab-panel__body">
            <div className="ab-toolbar">
              <div className="ab-field">
                <label>Month</label>
                <input
                  type="month"
                  className="ab-input"
                  value={historyFilters.month}
                  onChange={(e) =>
                    setHistoryFilters((f) => ({ ...f, month: e.target.value, offset: 0 }))
                  }
                />
              </div>
              <div className="ab-field">
                <label>Status</label>
                <select
                  className="ab-select"
                  value={historyFilters.status}
                  onChange={(e) =>
                    setHistoryFilters((f) => ({ ...f, status: e.target.value, offset: 0 }))
                  }
                >
                  <option value="">All</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>
            </div>
            {historyPayload.bills.length === 0 ? (
              <EmptyState title="No history for this period" description="Try another month or status." />
            ) : (
              <>
                <div className="ab-table-wrap">
                  <table className="ab-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Bill date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyPayload.bills.map((bill) => (
                        <tr key={bill.id}>
                          <td>
                            <strong>{bill.employee_name || "—"}</strong>
                            <div className="ab-note">{bill.employee_code || ""}</div>
                          </td>
                          <td>{formatDate(bill.bill_date)}</td>
                          <td>{formatMoney(bill.amount)}</td>
                          <td>
                            <StatusBadge status={bill.status} />
                          </td>
                          <td>{bill.rejection_reason || bill.remarks || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <PaginationBar
                  total={historyPayload.total}
                  limit={PAGE_SIZE}
                  offset={historyFilters.offset}
                  onChange={(offset) => setHistoryFilters((f) => ({ ...f, offset }))}
                />
              </>
            )}
          </div>
        </div>
      ) : null}

      <Drawer
        open={Boolean(eligDrawer)}
        title="Fuel eligibility"
        subtitle={eligDrawer?.employee_name}
        onClose={() => !saving && setEligDrawer(null)}
        footer={
          <>
            <button type="button" className="ab-btn ab-btn--secondary" disabled={saving} onClick={() => setEligDrawer(null)}>
              Cancel
            </button>
            <button type="submit" form="fuel-elig-form" className="ab-btn ab-btn--primary" disabled={saving || !enabled}>
              {saving ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        {eligDrawer ? (
          <form id="fuel-elig-form" onSubmit={saveEligibility}>
            <label className="ab-check" style={{ marginBottom: "1rem" }}>
              <input
                type="checkbox"
                checked={eligDrawer.eligible}
                onChange={(e) => setEligDrawer((d) => ({ ...d, eligible: e.target.checked }))}
              />
              <span>
                <strong>Eligible for fuel reimbursement</strong>
                <small>Only eligible employees can submit petrol bills.</small>
              </span>
            </label>
            <div className="ab-field">
              <label htmlFor="fuel-limit">Monthly limit (optional)</label>
              <input
                id="fuel-limit"
                className="ab-input"
                type="number"
                min="0"
                step="0.01"
                placeholder="No limit"
                value={eligDrawer.monthly_limit}
                onChange={(e) => setEligDrawer((d) => ({ ...d, monthly_limit: e.target.value }))}
              />
            </div>
          </form>
        ) : null}
      </Drawer>

      <Drawer
        open={Boolean(billDrawer)}
        title={billDrawer?.mode === "edit" ? "Edit fuel bill" : "Add fuel bill"}
        subtitle="Pending until HR / Admin approves."
        onClose={() => {
          if (saving) return;
          setDuplicatePrompt(null);
          setBillDrawer(null);
        }}
        wide
        footer={
          <>
            <button
              type="button"
              className="ab-btn ab-btn--secondary"
              disabled={saving}
              onClick={() => {
                setDuplicatePrompt(null);
                setBillDrawer(null);
              }}
            >
              Cancel
            </button>
            <button type="submit" form="fuel-bill-form" className="ab-btn ab-btn--primary" disabled={saving || !enabled}>
              {saving ? "Saving…" : "Save bill"}
            </button>
          </>
        }
      >
        <form id="fuel-bill-form" className="ab-form-grid" onSubmit={saveBill}>
          {duplicatePrompt?.record && duplicatePrompt?.mode !== "approve" ? (
            <div className="ab-field ab-field--full">
              <div className="ab-banner" role="alert">
                <strong>Duplicate entry for {duplicatePrompt.employeeName || "this employee"}</strong>
                <p>
                  A bill already exists on {formatDate(duplicatePrompt.record.bill_date)} (
                  {formatMoney(duplicatePrompt.record.amount)}). Use the dialog to{" "}
                  <strong>Don&apos;t add</strong> or <strong>Allow duplicate</strong>.
                </p>
              </div>
            </div>
          ) : null}
          <div className="ab-field ab-field--full">
            <label>Employee</label>
            <select
              className="ab-select"
              required
              disabled={billDrawer?.mode === "edit"}
              value={billForm.employee_id}
              onChange={(e) => setBillForm((f) => ({ ...f, employee_id: e.target.value }))}
            >
              <option value="">Select employee</option>
              {employees.map((emp) => {
                const id = emp.employee_id || emp.id;
                const name =
                  emp.full_name ||
                  [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
                  emp.employee_code;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="ab-field">
            <label>Bill date</label>
            <input
              type="date"
              className="ab-input"
              required
              value={billForm.bill_date}
              onChange={(e) => setBillForm((f) => ({ ...f, bill_date: e.target.value }))}
            />
          </div>
          <div className="ab-field">
            <label>Amount</label>
            <input
              type="number"
              className="ab-input"
              min="0.01"
              step="0.01"
              required
              value={billForm.amount}
              onChange={(e) => setBillForm((f) => ({ ...f, amount: e.target.value }))}
            />
          </div>
          <div className="ab-field ab-field--full">
            <label>Bill number</label>
            <input
              className="ab-input"
              value={billForm.bill_number}
              onChange={(e) => setBillForm((f) => ({ ...f, bill_number: e.target.value }))}
              placeholder="Optional · used to detect duplicates"
            />
          </div>
          {billDrawer?.mode === "create" ? (
            <div className="ab-field ab-field--full">
              <label>Attachment</label>
              <input
                type="file"
                className="ab-input"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={(e) => setBillForm((f) => ({ ...f, attachment: e.target.files?.[0] || null }))}
              />
              {billForm.attachment ? (
                <p className="ab-note" style={{ marginTop: "0.35rem" }}>
                  Selected: {billForm.attachment.name}
                </p>
              ) : (
                <p className="ab-note" style={{ marginTop: "0.35rem" }}>
                  PDF, JPG, or PNG · max 10 MB
                </p>
              )}
            </div>
          ) : null}
          <div className="ab-field ab-field--full">
            <label>Remarks</label>
            <input
              className="ab-input"
              value={billForm.remarks}
              onChange={(e) => setBillForm((f) => ({ ...f, remarks: e.target.value }))}
              placeholder="Optional"
            />
          </div>
        </form>
      </Drawer>

      <Drawer
        open={Boolean(rejectDrawer)}
        title="Reject fuel bill"
        subtitle={rejectDrawer ? `${formatMoney(rejectDrawer.amount)} · ${formatDate(rejectDrawer.bill_date)}` : ""}
        onClose={() => !saving && setRejectDrawer(null)}
        footer={
          <>
            <button type="button" className="ab-btn ab-btn--secondary" disabled={saving} onClick={() => setRejectDrawer(null)}>
              Cancel
            </button>
            <button type="submit" form="fuel-reject-form" className="ab-btn ab-btn--danger" disabled={saving}>
              {saving ? "Rejecting…" : "Reject bill"}
            </button>
          </>
        }
      >
        <form id="fuel-reject-form" onSubmit={onReject}>
          <div className="ab-field">
            <label htmlFor="reject-reason">Rejection reason</label>
            <textarea
              id="reject-reason"
              className="ab-textarea"
              required
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Required"
            />
          </div>
        </form>
      </Drawer>

      <ConfirmShell
        open={Boolean(deleteTarget)}
        title="Delete this fuel bill?"
        message="Pending bills can be removed. This cannot be undone."
        confirmLabel="Delete bill"
        cancelLabel="Keep bill"
        danger
        loading={saving}
        onCancel={() => !saving && setDeleteTarget(null)}
        onConfirm={onDelete}
      />

      <DuplicateRecordDialog
        open={Boolean(duplicatePrompt?.record)}
        title={
          duplicatePrompt?.mode === "approve"
            ? "Duplicate bill already approved"
            : "Duplicate bill entry"
        }
        message={
          duplicatePrompt?.mode === "approve"
            ? `${duplicatePrompt?.employeeName || "This employee"} already has an approved fuel bill on ${formatDate(
                duplicatePrompt?.record?.bill_date
              )}. Review it below before approving another bill for the same day.`
            : `You are entering another bill for ${
                duplicatePrompt?.employeeName || duplicatePrompt?.record?.employee_name || "this employee"
              } on ${formatDate(duplicatePrompt?.record?.bill_date)}. Same employee and same date already exist.`
        }
        recordTitle={
          duplicatePrompt?.mode === "approve" ? "Already approved bill" : "Existing fuel bill"
        }
        statusLabel={String(duplicatePrompt?.record?.status || "").toUpperCase() || undefined}
        statusKey={duplicatePrompt?.record?.status}
        fields={[
          {
            label: "Employee",
            value: duplicatePrompt?.record?.employee_name || duplicatePrompt?.employeeName || "—",
          },
          {
            label: "Bill date",
            value: formatDate(duplicatePrompt?.record?.bill_date),
          },
          {
            label: "Amount",
            value: formatMoney(duplicatePrompt?.record?.amount),
          },
          {
            label: "Bill number",
            value: duplicatePrompt?.record?.bill_number || "—",
          },
        ]}
        loading={saving}
        cancelLabel={duplicatePrompt?.mode === "approve" ? "Don't approve" : "Don't add"}
        allowLabel={duplicatePrompt?.mode === "approve" ? "Approve anyway" : "Allow duplicate"}
        onCancel={() => !saving && setDuplicatePrompt(null)}
        onAllow={allowDuplicateBill}
      />
    </div>
  );
}
